import { Navigate } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import { ROUTES } from '@/constants/routes';
import { useWorkspace } from '@/features/company-portal/workspace/WorkspaceProvider';
import { readPendingInvitationToken } from '@/features/company-portal/workspace/workspaceStorage';
import { isPlatformStaff } from '@/features/platform-admin/platformCapabilities';
import { LoadingLayout } from '@/layouts/LoadingLayout';

/**
 * `/` for an authenticated user resolves to their home experience (Company module Stage 2 —
 * context-aware onboarding). Resolution order:
 *
 *  1. A pending invitation (Scenario B) — the invited user who just registered/signed in returns
 *     straight to the invitation landing page, bypassing onboarding entirely.
 *  2. Platform Admin keeps its fixed dashboard — the backend returns roles in assignment order, not
 *     priority order, so this is checked explicitly rather than taking roles[0].
 *  3. Business workspaces, from membership state:
 *       Candidate only → Candidate Workspace; Company only → Company Workspace (including a
 *       pending registration — Scenario C); both → the remembered workspace, else the chooser
 *       (Scenario D); neither → the onboarding chooser (Scenario A).
 *
 * No account ever lands on a blank page or a 403 from here.
 */
export function RootRedirect() {
  const { user } = useAuth();
  const workspace = useWorkspace();

  const pendingToken = readPendingInvitationToken();
  if (pendingToken) {
    return (
      <Navigate
        to={`${ROUTES.invitationAccept}?token=${encodeURIComponent(pendingToken)}`}
        replace
      />
    );
  }

  // Any platform tier lands in the admin console (ADR-0032), not just Super Admin. Without this a
  // newly granted Platform Admin or Support user would fall through to membership resolution, find no
  // workspace, and be told they have no access — while the API happily authorized them.
  if (isPlatformStaff(user?.roles)) return <Navigate to={ROUTES.admin} replace />;

  // The Recruiter and Vendor platform roles deliberately do not branch here. Recruiting happens in
  // the COMPANY workspace, reached through membership, and Vendor is unbuilt; neither role is granted
  // by any flow. A holder of either falls through to the same membership resolution as everyone else,
  // which is where their real access lives.

  if (workspace.loading) return <LoadingLayout />;

  const hasCandidate = workspace.hasCandidateWorkspace;
  const hasCompany = workspace.hasCompanyAccess;

  if (hasCandidate && hasCompany) {
    if (workspace.lastWorkspace?.kind === 'candidate') {
      return <Navigate to={ROUTES.candidate} replace />;
    }
    if (workspace.lastWorkspace?.kind === 'company') {
      return <Navigate to={ROUTES.company} replace />;
    }
    return <Navigate to={ROUTES.workspaces} replace />;
  }
  if (hasCompany) return <Navigate to={ROUTES.company} replace />;
  if (hasCandidate) return <Navigate to={ROUTES.candidate} replace />;

  return <Navigate to={ROUTES.welcome} replace />;
}
